// client/src/components/ScenarioDropdown.tsx
"use client";

import React, { useState, useEffect } from 'react';
import { useScenarios } from '@/contexts/ScenarioContext';
import { ChevronDown } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface ScenarioDropdownProps {
  selectedScenario: string | null;
  onScenarioChange: (scenarioId: string | null) => void;
  className?: string;
  formMode?: boolean;
  onMobileMenuClose?: () => void;
}

export default function ScenarioDropdown({ selectedScenario, onScenarioChange, className = '', formMode = false, onMobileMenuClose }: ScenarioDropdownProps) {
  const router = useRouter();
  const { scenarios, loading: contextLoading } = useScenarios();
  const [componentLoading, setComponentLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Sync component loading state with context loading
  useEffect(() => {
    setComponentLoading(contextLoading);
  }, [contextLoading]);

  const selectedScenarioData = scenarios.find(s => s.id === selectedScenario);

  if (componentLoading) {
 return (
 <div className={`relative ${className}`}>
 <div className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white min-w-48">
 <span className="text-sm text-gray-500">Loading scenarios...</span>
 </div>
 </div>
 );
 }

 return (
 <div className={`relative ${className}`}>
  {formMode && Array.isArray(scenarios) && scenarios.length === 1 ? (
    // Show read-only scenario name when only one scenario exists AND we're in form mode
    <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 min-w-48 h-10 flex items-center">
      <span className="text-sm truncate text-gray-900">
        {scenarios[0].name}
      </span>
      {scenarios[0].is_default && (
        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
          Default
        </span>
      )}
    </div>
  ) : (
    // Show dropdown when multiple scenarios exist OR not in form mode
    <>
      <button
        data-testid="scenario-dropdown"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary min-w-48 h-10 w-full"
      >
        <span className={`text-sm truncate text-left ${
          selectedScenarioData ? 'text-gray-900' : 'text-gray-900'
        }`}>
          {selectedScenarioData ? selectedScenarioData.name : 'Select Scenario'}
        </span>
        <ChevronDown 
          className={`ml-2 h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed sm:absolute right-0 sm:right-auto left-0 sm:left-auto z-20 mt-1 w-full sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {Array.isArray(scenarios) && scenarios.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => {
                  onScenarioChange(scenario.id);
                  setIsOpen(false);
                  onMobileMenuClose?.();
                  // ponytail: soft navigation only. DashboardContext's useEffect
                  // refreshes on selectedScenario change; a hard nav aborts the
                  // in-flight /api/batch and trips the error screen.
                  router.push('/dashboard');
                }}
                className={`w-full text-left px-4 py-2 text-sm  ${
                  selectedScenario === scenario.id 
                  ? 'bg-positive-100 text-positive-600 hover:bg-accent-primary-hover' 
                  : 'cursor-pointer text-gray-900 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{scenario.name}</span>
                  {scenario.is_default && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      Default
                    </span>
                  )}
                </div>
                {scenario.description && (
                  <p className="text-xs text-gray-500 mt-1 truncate text-left">{scenario.description}</p>
                )}
              </button>
            ))}
        
          
            {/* Manage Scenarios Button */}
            <div className="border-t border-gray-200 p-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  router.push('/scenarios'); // Use router.push for navigation
                }}
                className="cursor-pointer w-full text-left px-4 py-2 text-sm text-accent-primary hover:bg-accent-primary-hover focus:bg-accent-primary-hover focus:outline-none"
              >
                Manage Scenarios
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )}
 </div>
 );
}
